import { AudioIO, WebAudioIO } from './webAudioIO';

/** The minimum a WebSocket needs to offer here; lets tests use a fake. */
export interface MediaSocket {
  binaryType: BinaryType;
  readyState: number;
  onopen: ((event: Event) => void) | null;
  onmessage: ((event: MessageEvent) => void) | null;
  onclose: ((event: CloseEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  send(data: ArrayBufferLike | ArrayBufferView | string): void;
  close(code?: number, reason?: string): void;
}

export interface BridgeCallOptions {
  /** Origin of the channel-gateway, e.g. https://dev-channel-gateway.centraone.ai */
  mediaUrl: string;
  /** The agent's access token; the gateway checks they are the agent being rung. */
  token: string;
  audio?: AudioIO;
  openSocket?: (url: string) => MediaSocket;
  /** A call this agent placed to a customer, which stays with them. */
  outbound?: boolean;
}

type BridgeEvent = 'accept' | 'disconnect' | 'error';

const OPEN = 1;
/**
 * A media connection that drops mid-call is reopened this many times. The
 * server keeps the caller for a few seconds after the agent's audio goes
 * (VOICE_MEDIA_AGENT_GRACE_MS, 5 s), so the retries fit inside that.
 */
const MAX_RECONNECTS = 3;
const RECONNECT_DELAY_MS = 700;

/**
 * A call bridged by the platform (the caller's audio streams to us, e.g. from
 * Exotel). Offers the same accept / reject / disconnect / mute surface as a
 * Twilio Voice SDK call, so the console handles both the same way.
 *
 * Accepting opens the microphone, then the media socket. The server treats
 * the socket opening as the agent answering.
 */
export class BridgeCall {
  private socket?: MediaSocket;
  private readonly audio: AudioIO;
  private readonly listeners = new Map<BridgeEvent, Set<(detail?: unknown) => void>>();
  private ended = false;
  /** True once the media connection has opened: the call was answered. */
  private answered = false;
  private reconnects = 0;
  /** A bridged call can be passed to a colleague after it is answered. */
  readonly transferable: boolean;
  /** ...or have a colleague added to it (a 3-way call). */
  readonly conferenceable: boolean;

  constructor(
    readonly sessionId: string,
    readonly attemptId: string,
    private readonly options: BridgeCallOptions,
  ) {
    this.audio = options.audio ?? new WebAudioIO();
    this.transferable = !options.outbound;
    this.conferenceable = !options.outbound;
  }

  on(event: BridgeEvent, listener: (detail?: unknown) => void): () => void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(listener);
    return () => this.listeners.get(event)?.delete(listener);
  }

  /** The media URL for this call. */
  get url(): string {
    const origin = this.options.mediaUrl.replace(/\/+$/, '').replace(/^http/, 'ws');
    const query = new URLSearchParams({ access_token: this.options.token, attemptId: this.attemptId });
    return `${origin}/voice/media/agent/${encodeURIComponent(this.sessionId)}?${query.toString()}`;
  }

  async accept(): Promise<void> {
    if (this.ended || this.socket) return;
    try {
      // Microphone first: if it is refused, the server never sees an answer
      // and the caller moves on to the next agent.
      await this.audio.start((frame) => {
        if (this.socket?.readyState === OPEN) this.socket.send(frame.buffer as ArrayBuffer);
      });
    } catch (error) {
      this.emit('error', error);
      this.disconnect();
      return;
    }
    if (this.ended) {
      this.audio.stop();
      return;
    }

    this.connect();
  }

  private connect(): void {
    const socket = (this.options.openSocket ?? ((url) => new WebSocket(url) as MediaSocket))(this.url);
    socket.binaryType = 'arraybuffer';
    this.socket = socket;
    socket.onopen = () => {
      this.reconnects = 0;
      if (this.answered) return;
      this.answered = true;
      this.emit('accept');
    };
    socket.onmessage = (event) => this.onMessage(event.data);
    socket.onclose = (event) => this.onClosed(socket, event);
    socket.onerror = () => {
      // A dropped connection mid-call is retried in onClosed; only a call
      // that never connected is reported (the caller then moves on).
      if (!this.answered) this.emit('error', new Error('media connection failed'));
    };
  }

  /**
   * The media connection closed without either side hanging up. A call
   * already in progress reconnects - the network blinked, or a proxy dropped
   * the socket - rather than leaving the caller to hear a goodbye. The close
   * code is logged so a recurring cause can be found.
   */
  private onClosed(socket: MediaSocket, event?: CloseEvent): void {
    if (this.ended || socket !== this.socket) return;
    const code = event?.code;
    const reason = event?.reason;
    if (this.answered && this.reconnects < MAX_RECONNECTS) {
      this.reconnects += 1;
      console.warn(
        `[BridgeCall] media connection closed (code ${code ?? '?'}${reason ? `, ${reason}` : ''}); ` +
          `reconnecting (${this.reconnects}/${MAX_RECONNECTS})`,
      );
      setTimeout(() => {
        if (!this.ended && socket === this.socket) this.connect();
      }, RECONNECT_DELAY_MS);
      return;
    }
    console.warn(`[BridgeCall] media connection closed (code ${code ?? '?'}${reason ? `, ${reason}` : ''}); call ended`);
    this.disconnect();
  }

  /** Decline before answering. The server is told separately (decline API). */
  reject(): void {
    this.disconnect();
  }

  disconnect(): void {
    if (this.ended) return;
    this.ended = true;
    // Hanging up a call whose audio is still connecting is never what an
    // agent meant to do; say where it came from so it can be found.
    if (this.socket && this.socket.readyState !== OPEN && this.socket.readyState !== 3) {
      console.warn('[BridgeCall] call ended while its audio was still connecting', new Error().stack);
    }
    try {
      this.socket?.close(1000, 'hangup');
    } catch {
      // already closing
    }
    this.audio.stop();
    this.emit('disconnect');
  }

  mute(muted: boolean): void {
    this.audio.setMuted(muted);
  }

  private onMessage(data: unknown): void {
    if (data instanceof ArrayBuffer) {
      this.audio.play(new Int16Array(data));
      return;
    }
    if (typeof data !== 'string') return;
    let control: { type?: string; attemptId?: string } = {};
    try {
      control = JSON.parse(data);
    } catch {
      return;
    }
    // An `end` addressed to someone else's attempt: the colleague who
    // transferred this call here is being told THEIR part is over, on a
    // channel both consoles can hear. It is not this call ending.
    if (control.type === 'end' && control.attemptId && control.attemptId !== this.attemptId) return;
    // The caller left, or the server ended the call (re-routed, timed out).
    if (control.type === 'caller_left' || control.type === 'end') {
      console.info(`[BridgeCall] call ended by the server: ${control.type}`);
      this.disconnect();
    }
  }

  private emit(event: BridgeEvent, detail?: unknown): void {
    this.listeners.get(event)?.forEach((listener) => listener(detail));
  }
}
